/**
 * Cell Filters Context (Seeds)
 *
 * Provides the Seeds table filter state to cell components without prop drilling.
 * Mirrors the Assets/Vulnerabilities CellFiltersContext pattern so cells (e.g.
 * OriginCell) can opt in to filter awareness. Cells are rendered from config in
 * `config/columnDefinitions.tsx` and therefore have no direct props access — the
 * provider is mounted around `<SeedsTable>` in `sections/seeds/index.tsx`.
 *
 * @module seeds/CellFiltersContext
 */

import { createContext, useContext } from 'react';

import type { SeedFilter } from '@/hooks/useSeeds';

/**
 * Context for providing seed filter state to cell components
 */
const CellFiltersContext = createContext<SeedFilter | undefined>(undefined);

/**
 * Provider component for seed cell filter context
 */
export const CellFiltersProvider = CellFiltersContext.Provider;

/**
 * Hook to access seed filter state from cell components
 *
 * @returns Current seed filter state or undefined if not within provider
 *
 * @example
 * ```typescript
 * const filters = useCellFilters();
 * const activeOriginFilters = filters?.advancedFilters?.origins;
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook exported alongside provider, mirrors asset/vuln CellFiltersContext
export const useCellFilters = () => {
  return useContext(CellFiltersContext);
};
